import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/App";
import axios from "axios";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  ArrowLeft,
  User,
  Star,
  Shield,
  Droplets,
  Phone,
  Plus,
  Trash2,
  LogOut,
  Award,
  MapPin,
  Clock,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ProfilePage() {
  const navigate = useNavigate();
  const { user, logout, token } = useAuth();
  const [reviews, setReviews] = useState([]);
  const [emergencyContacts, setEmergencyContacts] = useState([]);
  const [newContact, setNewContact] = useState({ name: "", phone: "" });
  const [loading, setLoading] = useState(true);
  const [showAddContact, setShowAddContact] = useState(false);

  useEffect(() => {
    fetchUserData();
  }, []);

  const fetchUserData = async () => {
    try {
      const [reviewsRes, contactsRes] = await Promise.all([
        axios.get(`${API}/users/${user.user_id}/reviews`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
        axios.get(`${API}/emergency-contacts`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
      ]);
      setReviews(reviewsRes.data);
      setEmergencyContacts(contactsRes.data);
    } catch (error) {
      console.error("Error fetching user data:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddContact = async (e) => {
    e.preventDefault();
    if (!newContact.name || !newContact.phone) {
      toast.error("Please fill in all fields");
      return;
    }

    try {
      const response = await axios.post(
        `${API}/emergency-contacts`,
        newContact,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setEmergencyContacts([...emergencyContacts, response.data]);
      setNewContact({ name: "", phone: "" });
      setShowAddContact(false);
      toast.success("Emergency contact added");
    } catch (error) {
      toast.error("Failed to add contact");
    }
  };

  const handleDeleteContact = async (contactId) => {
    try {
      await axios.delete(`${API}/emergency-contacts/${contactId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setEmergencyContacts(emergencyContacts.filter((c) => c.contact_id !== contactId));
      toast.success("Contact removed");
    } catch (error) {
      toast.error("Failed to remove contact");
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate("/");
    toast.success("Logged out successfully");
  };

  const getRatingColor = (rating) => {
    if (rating >= 4) return "text-green-500";
    if (rating >= 3) return "text-amber-500";
    return "text-red-500";
  };

  return (
    <div className="min-h-screen bg-slate-50 pb-8" data-testid="profile-page">
      {/* Header */}
      <div className="bg-white border-b border-slate-100 sticky top-0 z-10">
        <div className="max-w-2xl mx-auto px-4 py-4 flex items-center justify-between">
          <button
            onClick={() => navigate("/")}
            className="flex items-center text-slate-500 hover:text-slate-700"
          >
            <ArrowLeft size={20} className="mr-2" />
            Back
          </button>
          <h1 className="text-lg font-bold">Profile</h1>
          <Button
            variant="ghost"
            size="sm"
            onClick={handleLogout}
            className="text-red-500 hover:text-red-600 hover:bg-red-50"
            data-testid="logout-btn"
          >
            <LogOut size={18} />
          </Button>
        </div>
      </div>

      <div className="max-w-2xl mx-auto px-4 pt-6 space-y-6">
        {/* User Info Card */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-start gap-4">
                <Avatar className="w-16 h-16">
                  <AvatarImage src={user?.picture} />
                  <AvatarFallback className="bg-sky-100 text-sky-600 text-xl">
                    {user?.name?.charAt(0).toUpperCase()}
                  </AvatarFallback>
                </Avatar>
                <div className="flex-1">
                  <h2 className="text-xl font-bold text-slate-900">{user?.name}</h2>
                  <p className="text-slate-500 text-sm">{user?.email}</p>
                  <div className="flex gap-2 mt-3 flex-wrap">
                    <Badge variant="secondary" className="text-xs">
                      <Star size={12} className="mr-1" />
                      {user?.reviews_count || 0} Reviews
                    </Badge>
                    {user?.badges?.map((badge) => (
                      <Badge key={badge} className="text-xs bg-sky-100 text-sky-700">
                        <Award size={12} className="mr-1" />
                        {badge}
                      </Badge>
                    ))}
                    {user?.role === "admin" && (
                      <Badge className="text-xs bg-purple-100 text-purple-700">Admin</Badge>
                    )}
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </motion.div>

        {/* Emergency Contacts */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
        >
          <Card>
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-lg flex items-center gap-2">
                <Phone size={18} className="text-red-500" />
                Emergency Contacts
              </CardTitle>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setShowAddContact(!showAddContact)}
                className="text-sky-600"
              >
                <Plus size={18} />
              </Button>
            </CardHeader>
            <CardContent>
              {showAddContact && (
                <form onSubmit={handleAddContact} className="mb-4 p-4 bg-slate-50 rounded-xl space-y-3">
                  <div>
                    <Label className="text-sm">Name</Label>
                    <Input
                      value={newContact.name}
                      onChange={(e) => setNewContact({ ...newContact, name: e.target.value })}
                      placeholder="Contact name"
                      className="mt-1"
                      data-testid="contact-name-input"
                    />
                  </div>
                  <div>
                    <Label className="text-sm">Phone</Label>
                    <Input
                      value={newContact.phone}
                      onChange={(e) => setNewContact({ ...newContact, phone: e.target.value })}
                      placeholder="+91 9876543210"
                      className="mt-1"
                      data-testid="contact-phone-input"
                    />
                  </div>
                  <Button type="submit" size="sm" className="w-full" data-testid="add-contact-btn">
                    Add Contact
                  </Button>
                </form>
              )}

              {emergencyContacts.length === 0 ? (
                <p className="text-slate-500 text-sm text-center py-4">
                  No emergency contacts added yet. Add contacts to be notified in case of SOS.
                </p>
              ) : (
                <div className="space-y-2">
                  {emergencyContacts.map((contact) => (
                    <div
                      key={contact.contact_id}
                      className="flex items-center justify-between p-3 bg-slate-50 rounded-xl"
                    >
                      <div>
                        <p className="font-medium text-slate-900">{contact.name}</p>
                        <p className="text-sm text-slate-500">{contact.phone}</p>
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDeleteContact(contact.contact_id)}
                        className="text-red-500 hover:text-red-600 hover:bg-red-50"
                      >
                        <Trash2 size={16} />
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </motion.div>

        {/* Review History */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
        >
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-lg flex items-center gap-2">
                <Star size={18} className="text-amber-500" />
                Your Reviews
              </CardTitle>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="space-y-3">
                  {[1, 2, 3].map((i) => (
                    <div key={i} className="skeleton h-20 rounded-xl" />
                  ))}
                </div>
              ) : reviews.length === 0 ? (
                <p className="text-slate-500 text-sm text-center py-4">
                  You haven't submitted any reviews yet.
                </p>
              ) : (
                <ScrollArea className="max-h-80">
                  <div className="space-y-3 pr-2">
                    {reviews.map((review) => (
                      <div
                        key={review.review_id}
                        className="p-4 bg-slate-50 rounded-xl"
                      >
                        <div className="flex justify-between items-start">
                          <div>
                            <div className="flex items-center gap-3">
                              <div className="flex items-center gap-1">
                                <Shield size={14} className={getRatingColor(review.safety_rating)} />
                                <span className="text-sm font-medium">{review.safety_rating}/5</span>
                              </div>
                              <div className="flex items-center gap-1">
                                <Droplets size={14} className={getRatingColor(review.cleanliness_rating)} />
                                <span className="text-sm font-medium">{review.cleanliness_rating}/5</span>
                              </div>
                            </div>
                            {review.comment && (
                              <p className="text-sm text-slate-600 mt-2">{review.comment}</p>
                            )}
                          </div>
                          <span className="text-xs text-slate-400 flex items-center gap-1">
                            <Clock size={12} />
                            {new Date(review.created_at).toLocaleDateString()}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                </ScrollArea>
              )}
            </CardContent>
          </Card>
        </motion.div>
      </div>
    </div>
  );
}
